import { useContext, useState } from "react";
import styled from "styled-components";
import { useMutation } from "react-query";
import { GraphQLClient } from "graphql-request";
import { Body3 } from "components/Typography";
import { Spinner } from "components";
import { useGetPeriodoEiduActivo } from "hooks/useRequest";
import {
  mapEiduFormValues,
  mapGruposToTalleresOptions,
} from "utilites/eiduForm";
import AlertContext from "context/AlertContext";
import { Paso1Identificacion } from "./Paso1";
import { Paso2Nuevo } from "./Paso2Nuevo";
import { Paso2Existente } from "./Paso2Existente";
import { EiduHero } from "./EiduHero";
import { asignarTallerEidu, buscarInscritoEidu, inscribirEidu } from "./mutation";

const API_URL = process.env.REACT_APP_API_URL as string;
const graphQLClient = new GraphQLClient(API_URL);
const EIDU_HOME_URL = "https://eidu.ucr.ac.cr/";
const EIDU_THANK_YOU_MSG =
  "Gracias por su inscripción. Próximamente recibirá un correo con las indicaciones " +
  "para su participación en EIDU 2026. Si tiene alguna consulta puede escribirnos a eidu@urosario.edu.co";

type Step1Data = { nombre: string; pais: string; identificacion: string };

type Mode = "paso1" | "nuevo" | "existente" | "gracias";

export default function EiduInscripcion() {
  const alert = useContext(AlertContext);
  const {
    data: periodo,
    isLoading,
    isError,
    refetch: refetchPeriodoEidu,
  } = useGetPeriodoEiduActivo();
  const [mode, setMode] = useState<Mode>("paso1");
  const [step1, setStep1] = useState<Step1Data | null>(null);
  const [inscrito, setInscrito] = useState<any>(null);
  const [isBuscando, setIsBuscando] = useState(false);

  const talleres = mapGruposToTalleresOptions(periodo?.grupoSet ?? []);

  const tallerActualId = inscrito?.matriculadoSet?.[0]?.grupo?.id
    ? Number(inscrito.matriculadoSet[0].grupo.id)
    : undefined;

  const mutationInscribir = useMutation(
    async (variables: Record<string, unknown>) => {
      const data = await graphQLClient.request(inscribirEidu, variables);
      return data.crearDocente;
    },
    {
      onSuccess: () => {
        setMode("gracias");
      },
      onError: (error: any) => {
        const message =
          error?.response?.errors?.[0]?.message ??
          "No se pudo completar la inscripción. Intente nuevamente.";
        /*@ts-ignore*/
        alert.error(message);
      },
    }
  );

  const mutationTaller = useMutation(
    async (variables: { idDocente: number; grupo: number | null }) => {
      const data = await graphQLClient.request(asignarTallerEidu, variables);
      return data.asignarTallerEidu;
    },
    {
      onSuccess: () => {
        setMode("gracias");
      },
      onError: (error: any) => {
        const message =
          error?.response?.errors?.[0]?.message ??
          "No se pudo asignar el taller. Intente nuevamente.";
        /*@ts-ignore*/
        alert.error(message);
      },
    }
  );

  const handlePaso1 = async (values: Step1Data) => {
    if (!periodo?.id) return;
    setIsBuscando(true);
    try {
      const data = await graphQLClient.request(buscarInscritoEidu, {
        identificacion: String(values.identificacion).replace(/\D/g, ""),
      });
      const encontrado = data.inscritoEidu;
      setStep1({
        nombre: values.nombre,
        pais: values.pais,
        identificacion: String(values.identificacion).replace(/\D/g, ""),
      });
      if (encontrado) {
        setInscrito(encontrado);
        setMode("existente");
      } else {
        setInscrito(null);
        setMode("nuevo");
      }
    } catch (error: any) {
      const message =
        error?.response?.errors?.[0]?.message ??
        "No se pudo consultar la inscripción. Intente nuevamente.";
      /*@ts-ignore*/
      alert.error(message);
    } finally {
      setIsBuscando(false);
    }
  };

  const handleNuevo = (values: any) => {
    if (!periodo?.id || !step1) return;
    mutationInscribir.mutate(
      mapEiduFormValues(
        {
          ...values,
          nombre: step1.nombre,
          pais: step1.pais,
          identificacion: step1.identificacion,
        },
        Number(periodo.id)
      )
    );
  };

  const handleExistente = (values: { taller?: number }) => {
    if (!inscrito?.id) return;
    mutationTaller.mutate({
      idDocente: Number(inscrito.id),
      grupo: values.taller != null ? Number(values.taller) : null,
    });
  };

  const goBackPaso1 = () => {
    setMode("paso1");
    setInscrito(null);
  };

  if (isLoading) {
    return (
      <Page>
        <Spinner isLoading title="Cargando periodo EIDU" />
      </Page>
    );
  }

  if (isError || !periodo) {
    return (
      <Page>
        <Body3>
          La inscripción al EIDU no está habilitada en este momento. No hay un
          periodo de matrícula EIDU disponible.
        </Body3>
      </Page>
    );
  }

  if (mode === "gracias") {
    return (
      <Page>
        <EiduHero />
        <ThankYouBox>
          <Body3>
            <strong>{EIDU_THANK_YOU_MSG}</strong>
          </Body3>
          <Body3 style={{ marginTop: 24 }}>
            <a href={EIDU_HOME_URL} rel="noreferrer">
              Ir a la página del EIDU
            </a>
          </Body3>
        </ThankYouBox>
      </Page>
    );
  }

  return (
    <Page>
      <EiduHero />

      {mode === "paso1" && (
        <Paso1Identificacion
          onContinue={handlePaso1}
          isLoading={isBuscando}
        />
      )}

      {mode === "nuevo" && step1 && (
        <Paso2Nuevo
          step1={step1}
          talleres={talleres}
          onFinish={handleNuevo}
          onBack={goBackPaso1}
          onRefreshTalleres={() => {
            refetchPeriodoEidu();
          }}
          isSubmitting={mutationInscribir.isLoading}
        />
      )}

      {mode === "existente" && inscrito && (
        <Paso2Existente
          inscrito={inscrito}
          tallerActualId={tallerActualId}
          talleres={talleres}
          onFinish={handleExistente}
          onBack={goBackPaso1}
          onRefreshTalleres={() => {
            refetchPeriodoEidu();
          }}
          isSubmitting={mutationTaller.isLoading}
        />
      )}

      {(mutationInscribir.isLoading || mutationTaller.isLoading) && (
        <Spinner isLoading title="Procesando..." />
      )}
    </Page>
  );
}

const Page = styled.div`
  padding: 26px 16px;
  @media (min-width: 768px) {
    padding: 26px 86px;
  }
  @media (min-width: 1200px) {
    padding: 0px 100px;
    max-width: 1200px;
    margin: 0 auto;
  }
`;

const ThankYouBox = styled.div`
  max-width: 640px;
  margin-top: 32px;

  a {
    color: #0bbbef;
    word-break: break-all;
  }
`;
